import { useCallback } from "react";
import { useRouter } from "next/router";
import posthog from "posthog-js";

import { identifyKlaviyo } from "@/analytics/klaviyo";
import { useSectionId } from "@/analytics/SectionContext";
import type { LandingPackageId, LandingPageId } from "@/cart/landingPackage";
import type { SideCartTrigger } from "@/stores/useLayoutStore";
import type { SignupWidget } from "@/utils/acquisition";

/** Route slugs. Property values, so they stay greppable against `$pathname`. */
export type PageId =
  | "home"
  | "shop"
  | "life-with-matic"
  | "innovation"
  | "hey-matic"
  | "accessories"
  | "accessory"
  | "accessibility"
  | "privacy-choices"
  | "careers"
  | "order-status"
  | "company"
  | "serene"
  | "lp-pet"
  | "lp-everyday"
  | "lp-switch"
  | "lp-homefit"
  | "lp-offer";

export type SectionId =
  // home
  | "hero"
  | "hey-matic-banner"
  | "how-it-works"
  | "press"
  | "testimonials"
  | "life-cross-link"
  | "buy-panel"
  | "blog-teasers"
  | "careers-banner"
  // shop
  | "shop-hero"
  | "configurator"
  | "whats-inside"
  | "reviews"
  | "innovation-banner"
  | "faqs"
  | "support-cards"
  | "social"
  // ad-to-page landing pages (/lp/*), shared by every page
  | "lp-hero"
  | "lp-proof"
  | "lp-why"
  | "lp-owner"
  | "lp-upkeep"
  | "lp-package"
  | "lp-faq"
  | "lp-closing"
  // innovation
  | "innovation-hero"
  | "pillar-intelligence"
  | "pillar-design"
  | "pillar-cleaning"
  | "roadmap"
  | "battlecard"
  | "quotes"
  | "innovation-careers"
  // life with matic
  | "life-hero"
  | "life-wake-up-ahead"
  | "life-rushing-out"
  | "life-come-home"
  | "life-time-to-eat"
  | "life-trustworthy-help"
  // careers
  | "careers-hero"
  | "careers-mission"
  | "careers-video"
  | "careers-stats"
  | "careers-culture"
  | "careers-press"
  | "careers-founder-navneet"
  | "careers-founder-mehul"
  | "careers-quotes"
  | "careers-roles-hero"
  | "careers-open-roles"
  // company
  | "company-manifesto"
  | "company-purpose"
  | "company-vision"
  | "company-philosophy"
  | "company-story"
  | "company-founders"
  | "company-people"
  // hsa-fsa
  | "hsa-fsa-hero"
  | "hsa-fsa-payments"
  | "hsa-fsa-steps"
  | "hsa-fsa-dates"
  | "hsa-fsa-cta"
  | "hsa-fsa-faqs"
  // order status
  | "order-status-lookup"
  | "order-status-result"
  | "order-status-not-found"
  // serene
  | "serene-hero"
  | "serene-story"
  | "serene-moments"
  | "serene-banner"
  // chrome
  | "nav"
  | "footer"
  | "announcement";

/** The three design pillars, keyed off `InnovationTabId`. */
export const PILLAR_SECTION_IDS = {
  intelligence: "pillar-intelligence",
  design: "pillar-design",
  cleaning: "pillar-cleaning",
} as const satisfies Record<string, SectionId>;

/**
 * The five time-of-day vignettes, keyed off `LifeVignetteId`. Prefixed so the
 * section reads unambiguously next to the pillar and shop ids.
 */
export const LIFE_SECTION_IDS = {
  "wake-up-ahead": "life-wake-up-ahead",
  "rushing-out": "life-rushing-out",
  "come-home": "life-come-home",
  "time-to-eat": "life-time-to-eat",
  "trustworthy-help": "life-trustworthy-help",
} as const satisfies Record<string, SectionId>;

export type CtaId =
  | "shop"
  | "innovation"
  | "hey-matic"
  | "life-cross-link"
  | "blog-index"
  | "careers"
  | "open-roles"
  // shop
  | "reviews"
  | "roadmap"
  | "team"
  | "social";

/** The three floor packages in the configurator. */
export type PackageId =
  "one-matic-one-dock" | "one-matic-two-docks" | "two-matics-two-docks";

export type AddOnId =
  "annual-bag-pass" | "matic-care" | "builder-kit" | "pet-brush-roll";

/** The /product configurator's blocks, which drive its visual zone. */
export type ConfiguratorBlockId =
  "title" | "model" | "combination" | "addOns" | "checkout";

/** The slides the /product configurator's visual zone can show. */
export type ConfiguratorSlideId =
  | "orbit"
  | "overview-film"
  | "setup"
  | "annual-bag-pass"
  | "matic-care"
  | "builder-kit"
  | "pet-brush-roll";

/** The `model-visual-order` experiment's arms. */
export type ModelVisualOrderVariant = "control" | "test";

/** The ten parts called out in What's Inside. */
export type WhatsInsideItemId =
  | "matic"
  | "dock"
  | "hepa-bags"
  | "mop-roll"
  | "brush-roll"
  | "water-tank"
  | "declogging-tool"
  | "side-brush"
  | "descaling-pouch"
  | "hardwood-cleaner";

export type ReviewTabId =
  "general-love" | "pets" | "families" | "work-from-home" | "time-saved";

export type HsaFsaStepId = "select" | "eligibility" | "save";

/** The /lp/* FAQ questions, pet page first. */
export type LandingFaqQuestionId =
  | "accidents"
  | "after-year-one"
  | "arrival"
  | "bag-pass"
  | "brush-roll-replace"
  | "change-setup"
  | "codes"
  | "cords"
  | "differences"
  | "dock-per-floor"
  | "dock-spacing"
  | "eligibility"
  | "ends"
  | "extras"
  | "hair-wrap"
  | "litter-kibble"
  | "maps"
  | "multi-floor"
  | "multi-floor-bundle"
  | "multiple-floors"
  | "pet-dock"
  | "pick-up"
  | "privacy"
  | "rooms"
  | "rugs"
  | "scare-pet"
  | "stairs"
  | "sunken-rooms"
  | "thresholds"
  | "trial"
  | "upkeep"
  | "vacuum-mop"
  | "vacuum-only";

export type HsaFsaFaqId =
  | "partner"
  | "conditions"
  | "what-is-flex"
  | "how-to-pay"
  | "no-card"
  | "declined"
  | "multiple-cards";

export type FaqGroupId =
  | "living-with-matic"
  | "maintenance"
  | "app-and-privacy"
  | "ownership-and-support"
  | "matic-serene";

/**
 * The questions, ranked by open rate, are a list of buyer doubt; the Serene
 * three are new and not yet ranked.
 */
export type FaqQuestionId =
  | "safe-around-kids-and-pets"
  | "quiet"
  | "clean-in-the-dark"
  | "battery-life"
  | "climb-ramps"
  | "multi-floor-house"
  | "refill-water-tank"
  | "replace-hepa-bag"
  | "replace-rolls"
  | "self-cleaning-mop"
  | "iphone-and-android"
  | "privacy-and-security"
  | "warranty-and-returns"
  | "international-shipping"
  | "serene-sound"
  | "serene-cleaning"
  | "serene-upgrade";

export type SocialNetwork = "x" | "instagram" | "linkedin";

/**
 * Every tab `tab_selected` can report. The shop review tabs plus the three
 * innovation pillars — the pillar nav is a `SegmentedTabs` like the others, so
 * it is the same interaction differentiated by `page`, not a new event. If a
 * tab bar ever adds an id outside this set, the call site stops compiling
 * until it is added here.
 */
export type TabId =
  | ReviewTabId
  | "intelligence"
  | "design"
  | "cleaning"
  | "wake-up-ahead"
  | "rushing-out"
  | "come-home"
  | "time-to-eat"
  | "trustworthy-help";

/**
 * Where a nav interaction happened. `top_bar` is the row itself — the spec
 * listed only the panels, but the two top-level links live outside them.
 */
export type NavSurface =
  "top_bar" | "hover_panel" | "shop_panel" | "menu_sheet" | "collapsed_pill";

export type NavPanel = "resources" | "shop";

/** A section that renders more than one UI reports which one was seen. */
export type SectionVariant = "scrub" | "carousel";

/**
 * The tracking plan. It cannot drift from the code because it compiles.
 *
 * `page` and `section` are injected by [useTrack] and are deliberately absent
 * here — call sites must not be able to pass them.
 */
export type EventMap = {
  section_viewed: { position: number; variant?: SectionVariant };
  cta_clicked: { cta: CtaId; href: string; network?: SocialNetwork };
  card_clicked: { card_id: string; card_index: number; is_available: boolean };
  tab_selected: { tab: TabId; tab_index: number };
  carousel_advanced: {
    carousel: string;
    from_index: number;
    to_index: number;
    slide_id: string;
    /** A swipe/click, or the desktop scroll-scrub driving the carousel. */
    trigger: "user" | "scroll";
  };
  video_completed: { video_id: string };

  // ── careers ──
  /** A team chip on the open-roles filter. `category` is the Ashby team. */
  job_filter_selected: { category: string };
  /** A job row click-through to its Ashby posting. */
  job_posting_clicked: {
    job_id: string;
    title: string;
    team: string;
    href: string;
  };

  // ── shop ──
  /** Revealed preference on price: which home shape, and trading up or down. */
  configurator_package_selected: {
    package_id: PackageId;
    matic_count: number;
    dock_count: number;
    total_amount: number | null;
    /** 0-based order of changes, so a first glance reads differently to a switch. */
    selection_index: number;
    /** The landing-page package pre-selected; absent on /product. */
    offer_package_id?: LandingPackageId | null;
  };
  configurator_addon_toggled: {
    addon_id: AddOnId;
    is_selected: boolean;
    addon_amount: number;
    /** The bag pass locks on at two Matics; that is inclusion, not a toggle. */
    is_included: boolean;
    total_amount: number | null;
    offer_package_id?: LandingPackageId | null;
  };
  /**
   * Replaces the dying `pdp_add_to_cart_clicked` (3,730/month on master).
   * Fires on click, before the mutation, so failed and abandoned adds stay
   * visible instead of vanishing with the request.
   */
  /** The block crossing the visual zone's trigger line changed. */
  configurator_block_active: { block_id: ConfiguratorBlockId };
  /**
   * A slide came up in the visual zone, by scroll, selection or control.
   * `variant` is the `model-visual-order` arm the model set ran in.
   */
  visual_slide_viewed: {
    model: "matic" | "matic-serene";
    block_id: ConfiguratorBlockId;
    slide_id: ConfiguratorSlideId;
    variant: ModelVisualOrderVariant;
  };
  /**
   * A feature tab chosen in the visual zone (tab bar, phone chip, Overview
   * hotspot, or the card's close back to Overview). `tab_id` is free-form:
   * the tab list is content (FEATURE_TABS) and may change.
   */
  visual_tab_selected: { tab_id: string; model: "matic" | "matic-serene" };
  /** Half of a feature tab's clip has played. */
  visual_clip_viewed: {
    tab_id: string;
    clip_id: string;
    model: "matic" | "matic-serene";
  };
  /** An add-on card's body opened its details; selection is separate. */
  addon_details_opened: { addon_id: AddOnId };
  /** A way to pay picked at the top of the model block. */
  payment_tab_selected: { tab_id: "now" | "finance" | "hsa" };
  add_to_cart_clicked: {
    button_location:
      | "shop_configurator"
      | "shop_sticky_bar"
      // The landing buy box in the hero.
      | "landing_hero"
      // The landing pages' own CTAs below the hero, adding the hero's cart.
      | "landing_sticky_bar"
      | "landing_package"
      // The landing buy box's add-ons CTA, which makes the cart match it.
      | "landing_update_cart"
      | "landing_closing";
    package_id: PackageId;
    addon_ids: AddOnId[];
    total_amount: number | null;
    offer_package_id?: LandingPackageId | null;
  };
  /**
   * A hidden ad-to-page landing page opened, with the package it pre-selected
   * (null when the visitor's choice prices at standard). Joins ad → page →
   * package; the order side is the `_lp_*` cart attributes.
   */
  landing_page_viewed: {
    landing_page_id: LandingPageId;
    offer_package_id: LandingPackageId | null;
    utm_source: string | null;
    utm_campaign: string | null;
  };
  /**
   * An accessory detail page opened, or a landing page's Matic (handle
   * "matic"). The shop page itself doesn't fire it.
   */
  product_viewed: { handle: string; is_available: boolean };
  video_toggled: { is_playing: boolean };
  video_played: { video_id: string };
  video_sound_toggled: { video_id: string; is_sound_on: boolean };
  /** Desktop only — the mobile carousel is native scroll with no state. */
  hotspot_selected: { item_id: WhatsInsideItemId };
  /** Desktop only — mobile shows every review already. */
  reviews_expanded: { visible_count: number };
  /** Fires on open only, so the count is doubt and not fidgeting. */
  faq_opened: { question_id: FaqQuestionId; group_id: FaqGroupId };

  hsa_fsa_explainer_opened: { step_id: HsaFsaStepId };
  /** Separate from `faq_opened`, whose ids are the shop FAQ's. */
  hsa_fsa_faq_opened: { question_id: HsaFsaFaqId };
  /** A landing page FAQ question opened; closing isn't reported. */
  landing_faq_opened: { question_id: LandingFaqQuestionId };
  /** A landing page's two-sided package picked, e.g. the dock or the Bag Pass. */
  landing_package_picked: { offer_package_id: LandingPackageId };

  // ── side cart ──
  cart_opened: {
    trigger: SideCartTrigger;
    cart_value: number | null;
    item_count: number | null;
  };
  cart_dismissed: { trigger: "overlay" | "escape" | "close_button" };
  /** This control closes the cart mid-purchase, so it is a prime leak. */
  upsell_learn_more_clicked: { upsell: "matic-care" };

  // ── chrome ──
  footer_link_clicked: {
    href: string;
    label: string;
    column: string;
    is_external: boolean;
  };
  announcement_clicked: { href: string };
  /** The site's main email capture, previously unmeasured end to end. */
  // `landing_page_id` only on a landing page's offer field (`lp_offer`).
  email_signup_submitted: {
    source: SignupWidget;
    is_valid: boolean;
    landing_page_id?: LandingPageId;
  };
  email_signup_succeeded: {
    source: SignupWidget;
    landing_page_id?: LandingPageId;
  };
  email_signup_failed: {
    source: SignupWidget;
    error_message: string;
    landing_page_id?: LandingPageId;
  };
  /**
   * What someone opens a menu to look for is what they came for.
   *
   * `panel` is absent when the surface itself is the answer — the mobile sheet
   * opens on its own menu view, not on a panel, and reporting one there was
   * phantom data.
   */
  nav_opened: { surface: NavSurface; panel?: NavPanel };
  nav_link_clicked: {
    href: string;
    label: string;
    surface: NavSurface;
    nav_link_index: number;
  };
  nav_dismissed: {
    method: "escape" | "scrim" | "close_button" | "mouse_leave" | "scroll";
  };

  // ── accessibility contact form ──
  a11y_form_submitted: { has_screenshot: boolean };
  a11y_form_succeeded: { has_screenshot: boolean };
  a11y_form_failed: { error_message: string };
};

export type Track = <K extends keyof EventMap>(
  name: K,
  props: EventMap[K],
) => void;

const PAGE_BY_PATHNAME: Record<string, PageId> = {
  "/": "home",
  "/product": "shop",
  "/life-with-matic": "life-with-matic",
  "/innovation": "innovation",
  "/hey-matic": "hey-matic",
  "/accessories": "accessories",
  "/accessories/[product]": "accessory",
  "/accessibility": "accessibility",
  "/privacy-choices": "privacy-choices",
  "/careers": "careers",
  "/order-status": "order-status",
  "/company": "company",
  "/serene": "serene",
  "/lp/pet": "lp-pet",
  "/lp/everyday": "lp-everyday",
  "/lp/switch": "lp-switch",
  "/lp/homefit": "lp-homefit",
  "/lp/offer": "lp-offer",
};

/**
 * Events that mark a session worth watching in full. A flat 5% sample records
 * mostly nothing happening; these are the people who reached the configurator
 * or the buy button, so their sessions override the sample and record whole.
 */
const HIGH_INTENT_EVENTS: ReadonlySet<keyof EventMap> = new Set([
  "configurator_package_selected",
  "configurator_addon_toggled",
  "add_to_cart_clicked",
]);

/** `?ph_debug=1` console-logs every event with its final payload, which is how
 *  the instrumentation is QA'd by hand. */
const debugEnabled = (): boolean =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("ph_debug");

/**
 * Attach the user's email to their PostHog identity so downstream queries can
 * segment and reach these people by email. Called from every email-capture
 * surface once the signup request has been accepted.
 */
export const identifyEmail = (email: string): void => {
  posthog.identify(email, { email });
  identifyKlaviyo(email);
};

/**
 * Drop the identified person and start a fresh anonymous one, for a shared
 * device handing over to its next user — otherwise everything the next person
 * does before their own `identifyEmail` lands on the previous person.
 */
export const resetIdentity = (): void => {
  posthog.reset();
};

/**
 * The only place `posthog.capture` is called for new events.
 *
 * Returns a `track(name, props)` bound to the current page and section.
 */
export const useTrack = (): Track => {
  const router = useRouter();
  const pathname = router?.pathname ?? "";
  const section = useSectionId();

  return useCallback(
    (name, props) => {
      const payload = {
        ...props,
        // Blog posts became per-slug static pages; keep the historical
        // dashboard value they reported as a dynamic route.
        page:
          PAGE_BY_PATHNAME[pathname] ??
          (pathname.startsWith("/blog/") ? "/blog/[blog_url]" : pathname),
        ...(section === undefined ? {} : { section }),
      };
      if (debugEnabled()) {
        console.log(`[ph] ${name}`, payload);
      }
      posthog.capture(name, payload);

      if (HIGH_INTENT_EVENTS.has(name) && !posthog.sessionRecordingStarted()) {
        // `sampling: true` overrides the sample decision for this session.
        posthog.startSessionRecording({ sampling: true });
      }
    },
    [pathname, section],
  );
};
